import { useCallback } from 'react';
import { useRootStackNavigation } from './useRootNavigation';
import {
  resolveWebviewWrappedRoute,
  WEBVIEW_WRAPPED_NATIVE_SCREENS,
  type ResolvedRoute,
} from '../webRoutePaths';

/**
 * `webRoutePaths` 기준으로 해석된 라우트를 RN 화면으로 push 하는 공용 훅.
 *
 * 딥링크 진입(`useOpenDeeplink`)과 명시적 웹뷰 이동(`useMoveToWebview`) 양쪽이 같은 규칙을
 * 쓰도록 한 곳에 모았다. 대상 목록은 `WEBVIEW_WRAPPED_NATIVE_SCREENS` 한 군데만 고치면 된다.
 */
export const useWebRoutePush = () => {
  const navigation = useRootStackNavigation();

  /**
   * 해석된 라우트로 push.
   *
   * push 는 screen 별 params 타입의 union 이라 screen 이 union 이면 오버로드가 narrow 되지
   * 않는다. webRoutePaths ↔ 각 ScreenItem contract 가 런타임에 매칭되는 게 보장되므로
   * 함수 타입만 넓혀 호출한다.
   */
  const pushResolvedScreen = useCallback(
    (resolved: ResolvedRoute) => {
      // web 도 네이티브와 같이 앱 안 화면으로 push 한다.
      //
      // 과거에는 WEBVIEW_WRAPPED_NATIVE_SCREENS(웹뷰 래퍼 화면)를 웹에서 새 탭으로 띄웠다.
      // window.open 이 RN 네비게이션 스택을 벗어나 뒤로가기·스택 복귀가 끊기는 문제가 커서
      // 앱 내 화면으로 되돌린다. 프레이밍(X-Frame-Options·CSP 없음)과 세션(쿠키 Domain=
      // .a-bly.com → mobile ↔ m 이 same-site) 모두 확인했다.
      const push = navigation.push as (
        screen: string,
        params: Record<string, unknown>,
      ) => void;

      // 웹뷰 래퍼 화면(Cart 등)은 route.params 를 그대로 웹뷰 URL 쿼리로 옮긴다
      // (Cart.screen.tsx). instanceId 를 넣으면 앱 내부 식별자가 콘텐츠 URL 에 붙고
      // push 마다 값이 달라져 주소·캐시가 흔들리므로 이 화면들에는 넣지 않는다.
      const isWebviewWrapped = WEBVIEW_WRAPPED_NATIVE_SCREENS.has(
        resolved.screen,
      );

      console.log('isWebviewWrapped', isWebviewWrapped, resolved);

      push(resolved.screen, {
        ...(isWebviewWrapped
          ? {}
          : { instanceId: `${resolved.screen}-${Date.now()}` }),
        ...resolved.pathParams,
        ...resolved.queryParams,
      });
    },
    [navigation],
  );

  /**
   * 웹뷰 URL 로 감싸져 온 목적지를 전용 화면으로 보낼 수 있으면 보낸다(보냈으면 true).
   *
   * 범용 WebViewScreen 으로 가면 목적지가 `url` params 문자열로 들어가서 react-navigation 이
   * 만드는 주소가 그 경로로 유지되지 않는다(뒤에 인코딩된 URL 이 붙는 형태).
   * 대상은 `WEBVIEW_WRAPPED_NATIVE_SCREENS` — 확장 시 그 목록에만 추가하면 된다.
   */
  const pushWebviewWrappedScreen = useCallback(
    (webviewUrl: string): boolean => {
      const route = resolveWebviewWrappedRoute(webviewUrl);
      if (route === null) return false;

      const push = navigation.push as (
        screen: string,
        params: Record<string, unknown>,
      ) => void;
      push(route.screen, route.params);
      return true;
    },
    [navigation],
  );

  return { pushResolvedScreen, pushWebviewWrappedScreen };
};
